import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Typography,
  CircularProgress,
  Button,
  Divider,
  Avatar,
  Tooltip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SecurityIcon from '@mui/icons-material/Security';
import PersonIcon from '@mui/icons-material/Person';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import apiService from '../services/api';

interface WebShieldAssistantProps {
  scanId: string;
  isEnabled: boolean;
}

interface Message {
  text: string;
  isBot: boolean;
  timestamp: Date;
}

const WebShieldAssistant: React.FC<WebShieldAssistantProps> = ({ scanId, isEnabled }) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [maximized, setMaximized] = useState<boolean>(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Add initial welcome message from the bot
  useEffect(() => {
    if (isEnabled && messages.length === 0) {
      setMessages([
        {
          text: "Hello! I'm WebShield Assistant. I can help you understand the vulnerabilities found in your scan and provide guidance on how to fix them. What would you like to know?",
          isBot: true,
          timestamp: new Date(),
        },
      ]);
    }
  }, [isEnabled, messages.length]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (expanded) {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, expanded]);

  // Focus input when expanded
  useEffect(() => {
    if (expanded) {
      inputRef.current?.focus();
    }
  }, [expanded]);

  const handleSendMessage = async () => {
    if (!message.trim() || !isEnabled) return;

    // Add user message to chat
    const userMessage: Message = {
      text: message,
      isBot: false,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setMessage('');
    setLoading(true);

    try {
      // Call API to get response
      const response = await apiService.sendChatMessage(scanId, message);
      
      // Add bot response to chat
      const botMessage: Message = {
        text: response.response,
        isBot: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      // Add error message if API call fails
      const errorMessage: Message = {
        text: "Sorry, I'm having trouble responding right now. Please try again later.",
        isBot: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatMessage = (text: string) => {
    // Convert markdown-style code blocks to styled text
    return text.split('```').map((part, index) => {
      if (index % 2 === 1) {
        // Code block
        return (
          <Paper
            key={index}
            elevation={0}
            sx={{
              p: 1.5,
              my: 1,
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              fontFamily: 'monospace',
              fontSize: maximized ? '1rem' : '0.85rem',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {part}
          </Paper>
        );
      } else {
        // Regular text
        return (
          <Typography
            key={index}
            variant="body1"
            sx={{ 
              whiteSpace: 'pre-wrap', 
              wordBreak: 'break-word',
              fontSize: maximized ? '1rem' : '0.9rem'
            }}
          >
            {part}
          </Typography>
        );
      }
    });
  };

  const toggleMaximized = () => {
    setMaximized(!maximized);
  };

  if (!isEnabled) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 20,
        right: 20,
        zIndex: 1000,
        width: expanded ? (maximized ? '550px' : '400px') : 'auto',
        maxWidth: maximized ? '90vw' : '80vw',
        boxShadow: 3,
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.3s ease',
      }}
    >
      {expanded ? (
        <Paper sx={{ 
          height: maximized ? '600px' : '500px', 
          display: 'flex', 
          flexDirection: 'column' 
        }}>
          {/* Chat header */}
          <Box
            sx={{
              p: 2,
              bgcolor: 'primary.main',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <SmartToyIcon sx={{ mr: 1 }} />
              <Typography variant="subtitle1">WebShield Assistant</Typography>
            </Box>
            <Box>
              <IconButton
                size="small"
                color="inherit"
                onClick={toggleMaximized}
                title={maximized ? "Reduce size" : "Increase size"}
              >
                {maximized ? <ZoomOutIcon /> : <ZoomInIcon />}
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => setExpanded(false)}
              >
                <ExpandMoreIcon />
              </IconButton>
              <IconButton
                size="small"
                color="inherit"
                onClick={() => {
                  setMessages([]);
                  setExpanded(false);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Chat messages */}
          <Box
            sx={{
              p: 2,
              flexGrow: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {messages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  alignSelf: msg.isBot ? 'flex-start' : 'flex-end',
                  maxWidth: '85%',
                }}
              >
                {msg.isBot && (
                  <Avatar
                    sx={{
                      bgcolor: 'primary.main',
                      width: maximized ? 40 : 32,
                      height: maximized ? 40 : 32,
                      mr: 1,
                    }}
                  >
                    <SecurityIcon fontSize={maximized ? "medium" : "small"} />
                  </Avatar>
                )}
                <Paper
                  elevation={1}
                  sx={{
                    p: 1.5,
                    bgcolor: msg.isBot ? '#f5f5f5' : 'primary.light',
                    color: msg.isBot ? 'text.primary' : 'white',
                    borderRadius: 2,
                    position: 'relative',
                    fontSize: maximized ? '1rem' : '0.9rem',
                  }}
                >
                  {formatMessage(msg.text)}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      mt: 0.5,
                      color: msg.isBot ? 'text.secondary' : 'rgba(255,255,255,0.7)',
                      textAlign: 'right',
                      fontSize: maximized ? '0.8rem' : '0.75rem',
                    }}
                  >
                    {msg.timestamp.toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Paper>
                {!msg.isBot && (
                  <Avatar
                    sx={{
                      bgcolor: 'primary.dark',
                      width: maximized ? 40 : 32,
                      height: maximized ? 40 : 32,
                      ml: 1,
                    }}
                  >
                    <PersonIcon fontSize={maximized ? "medium" : "small"} />
                  </Avatar>
                )}
              </Box>
            ))}
            {loading && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  alignSelf: 'flex-start',
                }}
              >
                <Avatar
                  sx={{
                    bgcolor: 'primary.main',
                    width: maximized ? 40 : 32,
                    height: maximized ? 40 : 32,
                  }}
                >
                  <SecurityIcon fontSize={maximized ? "medium" : "small"} />
                </Avatar>
                <Paper
                  elevation={1}
                  sx={{
                    p: 2,
                    bgcolor: '#f5f5f5',
                    borderRadius: 2,
                  }}
                >
                  <CircularProgress size={maximized ? 24 : 20} thickness={4} />
                </Paper>
              </Box>
            )}
            <div ref={chatEndRef} />
          </Box>

          <Divider />

          {/* Chat input */}
          <Box
            sx={{
              p: 2,
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'background.paper',
            }}
          >
            <TextField
              fullWidth
              placeholder="Ask about vulnerabilities found..."
              variant="outlined"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyPress}
              disabled={loading}
              inputRef={inputRef}
              size={maximized ? "medium" : "small"}
              sx={{ mr: 1 }}
            />
            <IconButton
              color="primary"
              onClick={handleSendMessage}
              disabled={!message.trim() || loading}
              size={maximized ? "medium" : "small"}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      ) : (
        <Tooltip title="Ask WebShield Assistant" placement="left">
          <Button
            variant="contained"
            color="primary"
            startIcon={<SmartToyIcon />}
            onClick={() => setExpanded(true)}
            sx={{ borderRadius: 4, px: 2, py: 1, fontSize: '1rem' }}
          >
            WebShield AI
          </Button>
        </Tooltip>
      )}
    </Box>
  );
};

export default WebShieldAssistant; 